import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { normalizeEmail, friendlyAuthError } from '@/lib/normalizeEmail';
import { readFunctionErrorBody } from '@/lib/functionError';
import { classifySignupError, recordSignupSource } from '@/lib/onboardingTracking';
import { useQueryClient } from '@tanstack/react-query';


interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPasswordRecovery: boolean;
  clearPasswordRecovery: () => void;
  signUp: (email: string, password: string, firstName: string, lastName: string, userType: 'candidate' | 'company', phoneNumber: string, marketingConsent: boolean, companyName?: string, isAgency?: boolean) => Promise<{ error: any; errorCode?: import('@/lib/onboardingTracking').SignupErrorCode; needsEmailConfirmation?: boolean; userId?: string }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resendConfirmation: (email: string) => Promise<{ error: any }>;
  verifyEmailCode: (userId: string, code: string) => Promise<{ error: any; valid?: boolean }>;
  resendVerificationCode: (userId: string, email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef<string | null | undefined>(undefined);

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);

  useEffect(() => {
    // Detect recovery flow from URL early (before any redirect logic runs)
    const hash = window.location.hash;
    const search = window.location.search;
    if (
      hash.includes('type=recovery') ||
      hash.includes('type=password_recovery') ||
      search.includes('type=recovery')
    ) {
      setIsPasswordRecovery(true);
    }

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Handle expired refresh tokens gracefully
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('Token refresh failed — signing out');
          supabase.auth.signOut();
          window.location.href = '/auth';
          return;
        }

        // Detect password recovery — keep user on /auth to set new password
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }

        // Wisselt het account (of logt iemand uit)? Gooi alle opgehaalde
        // gegevens weg, zodat er nooit data van de vorige gebruiker blijft
        // hangen in beeld. Van "niet ingelogd" naar "ingelogd" wissen we niets:
        // er staat dan geen data van iemand anders in beeld, en wissen zou
        // lopende flows (zoals solliciteren + account aanmaken) onderbreken.
        const nextUserId = session?.user?.id ?? null;
        const prevUserId = lastUserIdRef.current;
        if (prevUserId !== nextUserId) {
          lastUserIdRef.current = nextUserId;
          if (prevUserId) queryClient.clear();
        }


        // Onboarding-meting: bron één keer koppelen aan een nieuw kandidaat-account.
        if (event === 'SIGNED_IN' && session?.user?.user_metadata?.user_type === 'candidate'
          && Date.now() - new Date(session.user.created_at).getTime() < 3 * 86400_000) {
          recordSignupSource();
        }

        setUser(session?.user ?? null);
        setSession(session);
        setLoading(false);
        
        // Clear dismissed banners on sign out so they reappear on next login
        if (event === 'SIGNED_OUT') {
          sessionStorage.removeItem('dismissedBanners');
          setIsPasswordRecovery(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (lastUserIdRef.current === undefined) lastUserIdRef.current = session?.user?.id ?? null;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch((error) => {
      // Handle invalid/expired refresh tokens
      if (error?.message?.includes('Refresh Token Not Found') || error?.message?.includes('refresh_token_not_found')) {
        console.warn('Invalid refresh token — signing out:', error.message);
        supabase.auth.signOut();
        window.location.href = '/auth';
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName: string, lastName: string, userType: 'candidate' | 'company', phoneNumber: string, marketingConsent: boolean, companyName?: string, isAgency?: boolean) => {
    try {
      const cleanEmail = normalizeEmail(email);

      // Route signup through our edge function which uses the admin API.
      // This avoids Supabase Auth's built-in confirmation email (which hits
      // the default `over_email_send_rate_limit` under normal traffic).
      // Our custom OTP verification email is sent instead.
      const { data, error } = await supabase.functions.invoke('signup-with-otp', {
        body: {
          email: cleanEmail,
          password,
          firstName,
          lastName,
          userType,
          phoneNumber,
          companyName: companyName || null,
          marketingConsent,
          isAgency: isAgency || false,
        }
      });

      if (error || !data?.success) {
        // On a non-2xx response `data` is null and the JSON body lives on the
        // error — read it back so we can show the real reason.
        const body = error ? await readFunctionErrorBody(error) : null;
        const errCode = (data as any)?.error ?? body?.error;
        let description = "Er ging iets mis, probeer het opnieuw.";
        if (errCode === 'email_exists') {
          description = "Dit e-mailadres is al geregistreerd. Log in of gebruik 'wachtwoord vergeten'.";
        } else if (errCode === 'weak_password') {
          const reason = (data as any)?.reason ?? body?.reason;
          description = reason === 'pwned'
            ? "Dit wachtwoord komt voor in bekende datalekken. Kies een uniek wachtwoord van minimaal 8 tekens met hoofdletters, cijfers en een speciaal teken."
            : "Kies een sterker wachtwoord: minimaal 8 tekens, met hoofdletters, cijfers en een speciaal teken.";
        } else if (errCode === 'invalid_email') {
          description = "Ongeldig e-mailadres.";
        } else if (body?.message) {
          description = friendlyAuthError(body.message);
        } else if (error?.message) {
          description = friendlyAuthError(error.message);
        }
        toast({
          title: "Registratie mislukt",
          description,
          variant: "destructive"
        });
        return {
          error: error ?? { message: errCode ?? 'signup_failed' },
          errorCode: classifySignupError(errCode, (data as any)?.reason ?? body?.reason, password.length),
        };
      }


      return {
        error: null,
        needsEmailConfirmation: true,
        userId: data.userId as string,
      };
    } catch (error: any) {
      toast({
        title: "Er ging iets mis",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const cleanEmail = normalizeEmail(email);

      // Server-side rate limit guard (max 5 failed logins per IP / 15 min).
      try {
        const { data: guard } = await supabase.functions.invoke('auth-login-guard', {
          body: { action: 'check' }
        });
        if (guard && guard.allowed === false) {
          const message = guard.error || "Te veel inlogpogingen. Probeer het later opnieuw.";
          toast({ title: "Even wachten", description: message, variant: "destructive" });
          return { error: { message } };
        }
      } catch {
        // Guard unavailable → fail open, Supabase Auth has its own limits.
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password
      });

      if (error) {
        void supabase.functions.invoke('auth-login-guard', { body: { action: 'failure' } });

        // Check if it's an email not confirmed error
        if (error.message.includes('Email not confirmed')) {
          toast({
            title: "Email nog niet bevestigd 📬",
            description: "We sturen je opnieuw naar de verificatiecode zodat je verder kunt.",
            variant: "destructive"
          });
          return { error: { ...error, needsConfirmation: true } };
        }
        
        toast({
          title: "Inloggen mislukt",
          description: friendlyAuthError(error.message),
          variant: "destructive"
        });
        return { error };
      }

      void supabase.functions.invoke('auth-login-guard', { body: { action: 'success' } });


      // Extra check: verify email is confirmed
      if (data.user && !data.user.email_confirmed_at) {
        await supabase.auth.signOut();
        toast({
          title: "Email nog niet bevestigd 📬",
          description: "We sturen je opnieuw naar de verificatiecode zodat je verder kunt.",
          variant: "destructive"
        });
        return { error: { message: "Email not confirmed", needsConfirmation: true } };
      }

      toast({
        title: "Welkom terug!",
        description: "Je bent succesvol ingelogd."
      });

      return { error: null };
    } catch (error: any) {
      toast({
        title: "Er ging iets mis",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
  };

  const resendConfirmation = async (email: string) => {
    try {
      const { error } = await supabase.functions.invoke('resend-confirmation-email', {
        body: { email: normalizeEmail(email) }
      });

      if (error) {
        toast({
          title: "Kon email niet versturen",
          description: error.message,
          variant: "destructive"
        });
        return { error };
      }

      toast({
        title: "Email verzonden! 📧",
        description: "Check je inbox voor de nieuwe bevestigingsmail."
      });

      return { error: null };
    } catch (error: any) {
      toast({
        title: "Er ging iets mis",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
  };

  const verifyEmailCode = async (userId: string, code: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-email-code', {
        body: { userId, code }
      });

      if (error) throw error;

      if (data?.valid) {
        // Refresh session to get updated email_confirmed_at
        await supabase.auth.refreshSession();
        return { error: null, valid: true };
      }

      return { error: { message: data?.error || "Invalid code" }, valid: false };
    } catch (error: any) {
      return { error, valid: false };
    }
  };

  const resendVerificationCode = async (userId: string, email: string) => {
    try {
      const { error } = await supabase.functions.invoke('resend-verification-code', {
        body: { userId, email: normalizeEmail(email) }
      });

      if (error) {
        toast({
          title: "Kon code niet versturen",
          description: error.message,
          variant: "destructive"
        });
        return { error };
      }

      return { error: null };
    } catch (error: any) {
      toast({
        title: "Er ging iets mis",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
  };

  const signOut = async () => {
    // Prevent multiple simultaneous signout calls
    if (isSigningOut) {
      return;
    }

    // If already signed out, just clear state
    if (!session && !user) {
      sessionStorage.removeItem('dismissedBanners');
      return;
    }

    try {
      setIsSigningOut(true);
      
      // Clear dismissed banners so they reappear on next login
      sessionStorage.removeItem('dismissedBanners');
      
      await supabase.auth.signOut();

      // Direct alle opgehaalde gegevens weggooien, zonder te wachten op het
      // uitlog-signaal. Zo blijft er nooit iets van de vorige gebruiker staan.
      queryClient.clear();

      
      toast({
        title: "Uitgelogd",
        description: "Tot snel!"
      });
    } catch (error: any) {
      // Ignore "session not found" errors as user is already logged out
      if (!error.message?.includes('Session not found') && !error.message?.includes('session_not_found')) {
        toast({
          title: "Uitloggen mislukt",
          description: error.message,
          variant: "destructive"
        });
      }
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isPasswordRecovery, clearPasswordRecovery, signUp, signIn, signOut, resendConfirmation, verifyEmailCode, resendVerificationCode }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
